"use client";
import React from "react";

import { Box, BoxSx, Target } from "../../foundations";
import { Icon } from "../../foundations/Icon/Icon";
import { Text } from "../../foundations/Typography/Text";
import { useId } from "../../hooks/useId";
import { useWarning } from "../../hooks/useWarning";
import { AccordionContext } from "./Accordion";

export interface AccordionItemProps {
  defaultOpen?: boolean;
  title: React.ReactNode;
  body?: string;
  children?: React.ReactNode;
}

export const AccordionItem: React.FC<AccordionItemProps> = (
  props: AccordionItemProps
) => {
  const { warn } = useWarning();
  const { body, children, title, defaultOpen } = props;
  const { toggleItem, getIsExpanded } = React.useContext(AccordionContext);
  const itemId = useId();
  if (title === null) {
    warn(
      "The `title` prop of a AccordionItem component is null. Please pass either a string or ReactNode."
    );
  }
  const isFirstRender = React.useRef(true);
  if (isFirstRender.current && defaultOpen) {
    toggleItem(itemId);
    isFirstRender.current = false;
  }

  const isOpen = getIsExpanded(itemId) || false;

  const contentRef = React.useRef<HTMLDivElement>(null);

  const targetSxProps: BoxSx = {
    fontWeight: "font-weight-medium",
    color: "text-default",
    px: "spacing-5",
    py: "spacing-4",
    borderTopStyle: "solid",
    borderTopWidth: "1px",
    borderTopColor: "stroke-neutral-tertiary",
    marginTop: "-1px",
    width: "100%",
    fontSize: "font-size-5",
    lineHeight: "leading-2"
  };

  if (contentRef.current) {
    if (isOpen) {
      contentRef.current.style.maxHeight =
        contentRef.current.scrollHeight + "px";
    } else {
      contentRef.current.style.maxHeight = "0";
    }
  }

  return (
    <>
      <Target
        sx={{
          ...targetSxProps,
          cursor: children ? "pointer" : "default"
        }}
        onClick={() => toggleItem(itemId)}
        aria-expanded={isOpen}
        aria-controls={`itemContent-${itemId}`}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between"
          }}
        >
          {title}
          {(children || body) && (
            <Icon
              size="sm"
              icon={isOpen ? "caret-up" : "caret-down"}
              sx={{
                color: "button-onSurface-subtle-enabled",
                minWidth: "spacing-6",
                paddingLeft: "spacing-2"
              }}
            />
          )}
        </Box>
      </Target>
      <Box
        ref={contentRef}
        sx={{
          overflow: "hidden",
          transition: () => "max-height 300ms cubic-bezier(0.4, 0, 0, 1)",
          borderBottomStyle: "solid",
          borderBottomWidth: "1px",
          borderBottomColor: "stroke-neutral-tertiary",
          maxHeight: defaultOpen ? undefined : 0
        }}
        id={`itemContent-${itemId}`}
      >
        {body && (
          <Box
            sx={{
              px: "spacing-5",
              pb: "spacing-4"
            }}
          >
            <Text variant="body-1-regular">{body}</Text>
          </Box>
        )}
        {children && (
          <Box
            sx={{
              px: "spacing-5",
              pb: "spacing-4"
            }}
          >
            {children}
          </Box>
        )}
      </Box>
    </>
  );
};
